import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import Body from '../Components/layout/Body';
import { SUBTABS } from './ConstPaths';
import TestDriveSetupSidebar from './TestDriveSetupSidebar';
import TestDriveDevSidebar from './TestDriveDevSidebar';

const TD_SETUP_INTRO_HELP = `
  <div>
    <p><strong>Purpose: </strong>Here you can define the setup for the access method you are going to Test-drive.  
    For example, you may be using a trackball, but that in itself doesn’t give the full picture of the setup.  
    What will you use for the single- and double-clicks?  Most trackballs have built-in buttons, but perhaps those aren’t very easy for the client to use.  
    What body sites will be used to control the trackball, and how? 
    Are there particular accessories that you’ll use, like maybe something worn on the hand to enhance control? 
    </p>
    <p>
    All of these things affect the client’s performance and experience with the access method, so it’s very useful to keep track of them as part of your Test-drives.    
    </p>
    <p>
    There are just a few questions in this section, and you’ll also be able to type in additional notes if there’s something our questions don’t cover.     
    </p>
  </div>
`
const propTypes = {
  activeAccessMethodName: PropTypes.string,
  activeSetupSelectionMethod: PropTypes.string,
  activeSetupAccessories: PropTypes.arrayOf(PropTypes.string),
  activeSetupBodySite: PropTypes.arrayOf(PropTypes.string),
  activeSetupName: PropTypes.string,
  onContinue: PropTypes.func.isRequired
};

const defaultProps = {
  activeAccessMethodName: '',
  activeSetupSelectionMethod: '',
  activeSetupBodySite: [],
  activeSetupAccessories: [],
  activeSetupName: '',
};

//content sub-component with unique content for this page
function IntroContent(props) {
  //these are displayed but can't be changed:
  const activeAccessMethodName = props.activeAccessMethodName;
  
  const am_path = SUBTABS.CHOOSE;
  return(
    <>
    {(activeAccessMethodName) ?
      <>
      <p>Now add a few more details about the setup.</p>
      <p>This includes defining things like:</p>
      <ul>
          <li>Item selection method</li>
          <li>Body site(s) for access</li>
          <li>Any control accessories</li>
          <li>Positioning and other notes</li>
      </ul>
      <p>This info is not <strong>required</strong>, but it helps us (and you!) keep track of multiple Test-drives with less confusion,
        and interpret the results more clearly.</p>
      </>
    : 
      <>
      <p><strong>Note:</strong> We recommend that you <Link to={am_path}>select an access method</Link> first. That way we can offer setup choices that relate to that access method. It also helps us (and you!) keep track of multiple Test-drives with less confusion.</p>
      <p>But you can continue without selecting an access method. The next few screens will ask about the setup.</p>
      <p>This includes defining things like:</p>
      <ul>
        <li>Item selection method</li>
        <li>Body site(s) for access</li>
        <li>Any control accessories</li>
        <li>Positioning and other notes</li>
      </ul>
      </>
    }
    </>
  );
}

//main component for this page
//basic pattern is to set up the pageData, define the unique content for the page (above),
//then render the page using <Body> component
function TestDriveSetupIntro(props) {
  const {
    activeAccessMethodName,
    activeSetupSelectionMethod,
    activeSetupBodySite,
    activeSetupAccessories,
    activeSetupName,
    onContinue,
  } = props;

  const dev = props.dev || {};

  console.log("rendering TestDriveSetupIntro, selection method: " + activeSetupSelectionMethod);

  const pageData = {
    image: "", //not yet used
    faIcon: "",
    pageSymbolClassName: "aa-section-symbol test-drive",
    title: `Let's define the setup for this access method`,
    subTitle: "",
    learnMore: {linkText: "Learn More about Test-drive setup",
                title: "Test-drive setup",
                content: TD_SETUP_INTRO_HELP
                },
    nextRoute: "", //not used since we have an onContinue callback
    sidebar: true
  }

  return(
    <>
    <Body page={pageData} 
      contentComponent={
        <IntroContent 
          activeAccessMethodName={activeAccessMethodName}
        />
      }
      sidebarComponent={
        //don't show the dev sidebar -- App.test depends on content of regular sidebar
        dev.showDevSidebar 
        ?
        <TestDriveDevSidebar 
          activeTestDriveId={dev.tdId}
          activeTestDrive={dev.td}
          activeTestDriveActivityId={dev.actId}
          activeTestDriveActivities={dev.acts}
          completedTestDrives={dev.ctds}
        />  
        :  
        <TestDriveSetupSidebar 
          activeAccessMethodName={activeAccessMethodName}
          activeSetupSelectionMethod={activeSetupSelectionMethod}
          activeSetupBodySite={activeSetupBodySite}
          activeSetupAccessories={activeSetupAccessories}
          activeSetupName={activeSetupName}
        />
      }
      onContinue={onContinue} 
    />
    </>
  );
}

TestDriveSetupIntro.propTypes = propTypes;
TestDriveSetupIntro.defaultProps = defaultProps;

export default TestDriveSetupIntro;